import { Box, Stack } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useCallback, useMemo } from "react";

import { VideoFrame } from "@/types";

import { getCurrentFrame, getVisibleFrames } from "../../../../utils/video";
import { Typography } from "../..";
import { NUM_FRAMES_TO_SHOW } from "../constants";
import { useScrubberDrag } from "../hooks";
import { formatTime } from "../utils/time";
import { VideoScrubberFrame } from "./VideoScrubberFrame";

const DragContainer = styled(Box)(() => ({
  position: "relative",
  height: 132, // 100px (tape) + 32px (wand extension)
  cursor: "pointer",
  userSelect: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
}));

const TimeLabel = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: 20,
}));

const ScrubberOuterContainer = styled(Box)(() => ({
  position: "relative",
  flex: 1,
  height: 100,
  margin: "0 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ScrubberWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 100,
  border: `2px solid ${theme.palette.secondary[4]}`,
  backgroundColor: theme.palette.secondary[8],
  borderRadius: 8,
  padding: 10,
  display: "flex",
  alignItems: "center",
}));

const VerticalLines = styled(Box)(({ theme }) => ({
  width: theme.spacing(2),
  height: 42,
  backgroundColor: "transparent",
  borderLeft: `2px solid ${theme.palette.secondary[1]}`,
  borderRight: `2px solid ${theme.palette.secondary[1]}`,
  flexShrink: 0,
}));

const ScrubberContent = styled(Box)(() => ({
  flex: 1,
  height: "100%",
  position: "relative",
}));

const ScrubberContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  border: `2px solid ${theme.palette.primary.dark}`,
  borderRadius: 12,
  overflow: "hidden",
  "&:hover": {
    borderColor: theme.palette.primary[5],
  },
}));

interface VideoScrubberProps {
  frames: VideoFrame[];
  currentTime: number;
  duration: number;
  onTimeChange: (time: number) => void;
}

export const VideoScrubber = (props: VideoScrubberProps) => {
  const { frames, currentTime, duration, onTimeChange } = props;

  const theme = useTheme();

  const {
    dragTime,
    scrubberRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleFrameMouseDown,
  } = useScrubberDrag({
    duration,
    onTimeChange,
  });

  const visibleFrames = useMemo(() => {
    return getVisibleFrames(frames, NUM_FRAMES_TO_SHOW);
  }, [frames]);

  // Calculate position based on time
  const getTimePosition = useCallback(
    (time: number) => {
      if (duration === 0) return 0;
      return (time / duration) * 100;
    },
    [duration],
  );

  const currentPosition = getTimePosition(dragTime ?? currentTime);

  const currentFrame = getCurrentFrame(frames, currentTime);
  const currentFramePosition = currentFrame
    ? getTimePosition(currentFrame.timestamp)
    : currentPosition;

  return (
    <Stack sx={{ width: "100%", maxWidth: 800 }}>
      <DragContainer
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <TimeLabel>
          <Typography color="secondary.3">{formatTime(currentTime)}</Typography>
        </TimeLabel>

        <ScrubberOuterContainer>
          <ScrubberWrapper>
            <VerticalLines sx={{ marginRight: theme.spacing(2) }} />
            <ScrubberContent>
              <ScrubberContainer ref={scrubberRef}>
                {/* Frames */}
                {visibleFrames.map((frame) => {
                  const framePosition = getTimePosition(frame.timestamp);
                  return (
                    <VideoScrubberFrame
                      key={`frame-${frame.index}`}
                      frame={frame}
                      position={framePosition}
                      onFrameMouseDown={handleFrameMouseDown}
                    />
                  );
                })}
              </ScrubberContainer>
            </ScrubberContent>
            <VerticalLines sx={{ marginLeft: theme.spacing(2) }} />
          </ScrubberWrapper>

          {currentFrame && (
            <Stack
              sx={{
                // TODO: Fix absolute positioning of the wand (may be too annoying)
                position: "absolute",
                left: `${currentFramePosition}%`,
                top: -8,
                width: 20,
                height: "calc(100% + 16px)",
                transform: "translateX(-50%)",
                zIndex: 10,
                pointerEvents: "none",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  width: 14,
                  height: 14,
                  backgroundColor: theme.palette.secondary[1],
                  clipPath: "polygon(50% 100%, 0 25%, 0 0, 100% 0, 100% 25%)",
                }}
              ></Box>
              <Box
                sx={{
                  width: 3,
                  height: "100%",
                  backgroundColor: theme.palette.secondary[1],
                }}
              />
            </Stack>
          )}
        </ScrubberOuterContainer>
        <TimeLabel>
          <Typography color="secondary.3">{formatTime(duration)}</Typography>
        </TimeLabel>
      </DragContainer>
    </Stack>
  );
};
